import React, { useCallback, useEffect, useState } from 'react'
import { PageTitle } from '../../../layout/core'
import ReactApexChart from 'react-apexcharts'
import axios from 'axios'
import LoadingSpinner from '../../LoadingSpinner'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import isBetween from 'dayjs/plugin/isBetween'
import { v4 as uuidv4 } from 'uuid'

const Forecast = () => {
  const [foreCastData, setForeCastData] = useState<any>(null)
  const [analyzeData, setAnalyzeData] = useState<any>(null)
  const [columns, setColumns] = useState<{
    dateColumns: string[]
    targetColumns: string[]
    issueColumns: string[]
  }>({
    dateColumns: [],
    targetColumns: [],
    issueColumns: []
  })
  const [selectedDateColumn, setSelectedDateColumn] = useState<string>('')
  const [selectedTargetColumn, setSelectedTargetColumn] = useState<string>('')
  const [selectedIssueColumn, setSelectedIssueColumn] = useState<any>('')
  const [selectedIssueTypeColumn, setSelectedIssueTypeColumn] = useState<string>('')
  const [issueTypes, setIssueTypes] = useState<string[]>([])

  const [startDate, setStartDate] = useState<string>('')
  const [endDate, setEndDate] = useState<string>('')
  const [filteredOriginalTrend, setFilteredOriginalTrend] = useState<any[]>([])

  const [forecastStartDate, setForecastStartDate] = useState<string>('')
  const [forecastEndDate, setForecastEndDate] = useState<string>('')
  const [filteredForecast, setFilteredForecast] = useState<any[]>([])
  const [selectedForecastType, setSelectedForecastType] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const navigate = useNavigate()
  dayjs.extend(isBetween)

  useEffect(() => {
    analyzeAPI()
  }, [])

  useEffect(() => {
    if (columns.dateColumns.length > 0 && columns.targetColumns.length > 0) {
      forecastApi()
    }
  }, [
    columns,
    selectedDateColumn,
    selectedTargetColumn,
    selectedIssueColumn,
    selectedIssueTypeColumn
  ])

  useEffect(() => {
    if (foreCastData?.original_trend?.dates.length > 0) {
      const trendDates = foreCastData.original_trend.dates
      setStartDate(trendDates[0])
      setEndDate(trendDates[trendDates.length - 1])
      setFilteredOriginalTrend(trendDates)
    }

    if (foreCastData?.['6_month']) {
      const forecastDates = Object.keys(foreCastData['6_month'])
      setForecastStartDate(forecastDates[0])
      setForecastEndDate(forecastDates[forecastDates.length - 1])
      setFilteredForecast(forecastDates)
    }
  }, [foreCastData])

  const forecastApi = async () => {
    setIsLoading(true)
    try {
      const response = await axios.get(
        `${
          process.env.REACT_APP_CSV_ANALYTICS_URL
        }/csv-analytics/forecast?filename=${localStorage.getItem(
          'uploadedFileName'
        )}&date_column=${selectedDateColumn}&target_column=${selectedTargetColumn}&issue_column=${selectedIssueColumn}&issue_type=${selectedIssueTypeColumn}`,
        { withCredentials: true }
      )

      setForeCastData(response.data)
      setIsLoading(false)
    } catch (error: any) {
      toast.warning(error.response?.data?.error || 'An error occurred')
      console.log(error, 'error')
      setIsLoading(false)
    }
  }

  const analyzeAPI = useCallback(async () => {
    try {
      const response = await axios.get(
        `${
          process.env.REACT_APP_CSV_ANALYTICS_URL
        }/csv-analytics/analyze?filename=${localStorage.getItem('uploadedFileName')}`,
        { withCredentials: true }
      )
      setAnalyzeData(response.data)
      console.log(response.data, 'analyzeResponse')
      const { data_types } = response.data
      const dateColumns = Object.keys(data_types).filter((col) => data_types[col] === 'Date')
      const targetColumns = Object.keys(data_types).filter((col) => data_types[col] === 'Numeric')
      const issueColumns = Object.keys(data_types).filter((col) => data_types[col] === 'Text')

      setColumns({ dateColumns, targetColumns, issueColumns })
    } catch (error) {
      console.log(error, 'error')
    }
  }, [])

  const handleDateColumnChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedDateColumn(event.target.value)
  }

  const handleTargetColumnChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedTargetColumn(event.target.value)
  }

  const handleIssueColumnChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedIssueColumn(event.target.value)
    setSelectedIssueTypeColumn('')

    const issueType = analyzeData?.text_values[event.target.value] || []
    setIssueTypes(issueType)
  }

  const handleIssueTypeColumnChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedIssueTypeColumn(event.target.value)
  }

  const handleStartDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(event.target.value)
    filterOriginalTrend(event.target.value, endDate)
  }

  const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(event.target.value)
    filterOriginalTrend(startDate, event.target.value)
  }

  const filterOriginalTrend = (start: string, end: string) => {
    const trendDates = foreCastData?.original_trend?.dates || []
    const filteredDates = trendDates.filter((date: string) =>
      dayjs(date).isBetween(start, end, null, '[]')
    )
    setFilteredOriginalTrend(filteredDates)
  }

  if (!foreCastData && (columns.dateColumns.length === 0 || columns.targetColumns.length === 0)) {
    return <LoadingSpinner />
  }

  const handleForecastStartDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setForecastStartDate(event.target.value)
    filterForecast(event.target.value, forecastEndDate)
  }

  const handleForecastEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setForecastEndDate(event.target.value)
    filterForecast(forecastStartDate, event.target.value)
  }

  const filterForecast = (start: string, end: string) => {
    const forecastDates = foreCastData?.['6_month'] ? Object.keys(foreCastData['6_month']) : []
    const filteredDates = forecastDates.filter((date: string) =>
      dayjs(date).isBetween(start, end, null, '[]')
    )
    setFilteredForecast(filteredDates)
  }

  const forecastAverage = filteredForecast.map(
    (date) => foreCastData?.['6_month'][date]?.average || null
  )

  const forecastLower = filteredForecast.map(
    (date) => foreCastData?.['6_month'][date]?.lower || null
  )

  const forecastUpper = filteredForecast.map(
    (date) => foreCastData?.['6_month'][date]?.upper || null
  )

  const xaxisCategories = [...filteredOriginalTrend, ...filteredForecast]

  const originalTrendValues =
    foreCastData?.original_trend?.values.slice(0, filteredOriginalTrend.length) || []

  const originalTrendSeries = {
    name: 'Original Trend',
    data: [...originalTrendValues]
  }

  const forecastAverageSeries = {
    name: 'Forecast',
    data: new Array(originalTrendValues.length).fill(null).concat(forecastAverage)
  }

  const forecastLowerSeries = {
    name: 'Forecast Min',
    data: new Array(originalTrendValues.length).fill(null).concat(forecastLower)
  }

  const forecastUpperSeries = {
    name: 'Forecast Max',
    data: new Array(originalTrendValues.length).fill(null).concat(forecastUpper)
  }

  const getFilteredForecastSeries = () => {
    if (selectedForecastType === 'Max') {
      return [forecastUpperSeries]
    } else if (selectedForecastType === 'Average') {
      return [forecastAverageSeries]
    } else if (selectedForecastType === 'Min') {
      return [forecastLowerSeries]
    } else {
      // If no type is selected or "All" is selected, return all series
      return [forecastUpperSeries, forecastAverageSeries, forecastLowerSeries]
    }
  }

  const series = [
    originalTrendSeries,
    ...getFilteredForecastSeries() // Add the filtered forecast series
  ]

  const options: any = {
    chart: {
      height: 350,
      type: 'line',
      dropShadow: {
        enabled: true,
        color: '#000',
        top: 18,
        left: 7,
        blur: 10,
        opacity: 0.2
      },
      toolbar: {
        show: false
      }
    },
    colors: ['#186df5', '#FF4560', '#00E396', '#FEB019'], // Colors for Original, Average, Max, and Min
    dataLabels: {
      enabled: false
    },
    stroke: {
      curve: 'smooth'
    },
    title: {
      text: 'Forecast',
      align: 'left'
    },
    grid: {
      borderColor: '#e7e7e7',
      row: {
        colors: ['#f3f3f3', 'transparent'], // alternating rows color
        opacity: 0.5
      }
    },
    markers: {
      size: 1
    },
    xaxis: {
      categories: xaxisCategories,
      labels: {
        show: false
      },
      title: {
        text: 'Period'
      }
    },
    yaxis: {
      title: {
        text: 'Value'
      }
    },
    legend: {
      position: 'top',
      horizontalAlign: 'right',
      floating: true,
      offsetY: -25,
      offsetX: -5
    }
  }

  return (
    <>
      <PageTitle breadcrumbs={[]}>Forecast</PageTitle>
      <div>
        <div
          className='card p-8'
          style={{ boxShadow: 'rgba(0, 0, 0, 0.16) 0px 2px 8px 0px' }}
          id='chart'
        >
          <div className='mb-4'>
            <i
              className='bi bi-arrow-left fs-1 fw-bold '
              onClick={() => navigate('/excel_table')}
              style={{ cursor: 'pointer' }}
              aria-hidden='true'
            ></i>
          </div>
          <div className='d-flex'>
            <div className='w-100 mw-150px mb-8 me-4' style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', top: '-9px', left: '5px', background: 'white' }}>
                <p style={{ margin: '0', fontSize: '13px' }}>Date Column</p>
              </div>
              <select
                className='form-select'
                onChange={handleDateColumnChange}
                value={selectedDateColumn}
              >
                <option value=''>Choose Option</option>
                {columns.dateColumns.map((col: string) => (
                  <option key={col} value={col}>
                    {col}
                  </option>
                ))}
              </select>
            </div>
            <div className='w-100 mw-150px mb-8 me-4' style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', top: '-9px', left: '5px', background: 'white' }}>
                <p style={{ margin: '0', fontSize: '13px' }}>Target Column</p>
              </div>
              <select
                className='form-select'
                onChange={handleTargetColumnChange}
                value={selectedTargetColumn}
              >
                <option value=''>Choose Option</option>
                {columns.targetColumns.map((col: string) => (
                  <option key={col} value={col}>
                    {col}
                  </option>
                ))}
              </select>
            </div>
            <div className='w-100 mw-150px mb-8 me-4' style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', top: '-9px', left: '5px', background: 'white' }}>
                <p style={{ margin: '0', fontSize: '13px' }}>Forecast Category</p>
              </div>
              <select
                className='form-select'
                onChange={handleIssueColumnChange}
                value={selectedIssueColumn}
              >
                <option value=''>-- Select Issue --</option>
                {columns.issueColumns.map(
                  (col: string) =>
                    !col?.includes('user_id') &&
                    !col?.includes('filename') && (
                      <option key={col} value={col}>
                        {col}
                      </option>
                    )
                )}
              </select>
            </div>
            <div className='w-100 mw-150px mb-8 me-4' style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', top: '-9px', left: '5px', background: 'white' }}>
                <p style={{ margin: '0', fontSize: '13px' }}>Forecast Subcategory </p>
              </div>
              <select
                className='form-control'
                id='issueTypeColumn'
                value={selectedIssueTypeColumn}
                onChange={handleIssueTypeColumnChange}
                disabled={!selectedIssueColumn} // Disable if no issue column is selected
              >
                <option value=''>-- Select Issue Type --</option>
                {issueTypes.map((type: string, index: number) => (
                  <option key={uuidv4()} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
            <div className='w-100 mw-150px mb-8 me-4' style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', top: '-9px', left: '5px', background: 'white' }}>
                <p style={{ margin: '0', fontSize: '13px' }}>Forecast Type</p>
              </div>
              <select
                id='selectedForecastType'
                className='form-control'
                value={selectedForecastType}
                onChange={(e) => setSelectedForecastType(e.target.value)}
              >
                <option value='All'>All</option>
                <option value='Average'>Average</option>
                <option value='Max'>Max</option>
                <option value='Min'>Min</option>
              </select>
            </div>
            {/* <div className='d-flex'> */}
            <div className='w-100 mw-200px mb-8 me-4' style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', top: '-9px', left: '5px', background: 'white' }}>
                <p style={{ margin: '0', fontSize: '13px' }}>Original Trend Start Date</p>
              </div>
              <input
                type='date'
                className='form-control'
                value={startDate}
                onChange={handleStartDateChange}
                min={foreCastData?.original_trend?.dates[0]}
                max={endDate}
              />
            </div>
            <div className='w-100 mw-200px mb-8 me-4' style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', top: '-9px', left: '5px', background: 'white' }}>
                <p style={{ margin: '0', fontSize: '13px' }}>Original Trend End Date</p>
              </div>
              <input
                type='date'
                className='form-control'
                value={endDate}
                onChange={handleEndDateChange}
                min={startDate}
                max={
                  foreCastData?.original_trend?.dates[
                    foreCastData?.original_trend?.dates.length - 1
                  ]
                }
              />
            </div>
            <div className='w-100 mw-200px mb-8 me-4' style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', top: '-9px', left: '5px', background: 'white' }}>
                <p style={{ margin: '0', fontSize: '13px' }}>Forecast Start Date</p>
              </div>
              <input
                type='date'
                className='form-control'
                value={forecastStartDate}
                onChange={handleForecastStartDateChange}
                min={Object.keys(foreCastData?.['6_month'] || {})[0]}
                max={forecastEndDate}
              />
            </div>
            <div className='w-100 mw-200px mb-8 me-4' style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', top: '-9px', left: '5px', background: 'white' }}>
                <p style={{ margin: '0', fontSize: '13px' }}>Forecast End Date</p>
              </div>
              <input
                type='date'
                className='form-control'
                value={forecastEndDate}
                onChange={handleForecastEndDateChange}
                min={forecastStartDate}
                max={
                  Object.keys(foreCastData?.['6_month'] || {})[
                    Object.keys(foreCastData?.['6_month'] || {}).length - 1
                  ]
                }
              />
              {/* </div> */}
            </div>
          </div>

          {/* Custom Legend */}
          {/* <div className='d-flex justify-content-end mb-4'>
            <div className='d-flex align-items-center me-4'>
              <span className='fs-1' style={{ color: '#186df5', marginRight: '8px' }}>
                ●
              </span>
              <span style={{ color: '#186df5' }}>Original Trend</span>
            </div>
            <div className='d-flex align-items-center me-4'>
              <span className='fs-1' style={{ color: '#33cc33', marginRight: '8px' }}>
                ●
              </span>
              <span style={{ color: '#33cc33' }}>Average Forecast</span>
            </div>
            <div className='d-flex align-items-center me-4'>
              <span className='fs-1' style={{ color: '#FF8C00', marginRight: '8px' }}>
                ●
              </span>
              <span style={{ color: '#FF8C00' }}>Max Forecast</span>
            </div>
            <div className='d-flex align-items-center me-4'>
              <span className='fs-1' style={{ color: '#DC143C', marginRight: '8px' }}>
                ●
              </span>
              <span style={{ color: '#DC143C' }}>Min Forecast</span>
            </div>
            {mape && (
              <div className='d-flex align-items-center'>
                <span>Accuracy: </span>
                <span className='fw-bold' style={{ marginLeft: '8px' }}>
                  {accuracy}
                </span>
              </div>
            )}
          </div> */}
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <ReactApexChart options={options} series={series} type='line' height={600} />
          )}
        </div>
      </div>
    </>
  )
}

export default Forecast
